/**
 * The crew's "Waiting to send (2)" chip and its list (D-1102-CREW-3): what is kept on the phone, what has just gone
 * ("Sent ✓"), and what the server refused ("Couldn't send — tell the office", with its words, a Try again and a way to
 * take it off the phone). Shown only when there is something to say.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { formatClockOf } from '@/lib/dates';
import { EARLIER_REFUSED, type QueuedAction } from './queue';
import { useOfflineQueue } from './OfflineQueueProvider';

export function refusalWords(t: (k: string, o?: Record<string, unknown>) => string, a: Pick<QueuedAction, 'message'>): string {
  return a.message === EARLIER_REFUSED ? t('crew1102.earlierRefused') : (a.message ?? '');
}

export function WaitingToSend() {
  const { t, i18n } = useTranslation();
  const q = useOfflineQueue();
  const [asked, setAsked] = useState<string | null>(null);
  if (!q.items.length && !q.sentNow.length) return null;
  const waiting = q.items.filter((a) => a.status === 'waiting');
  const refused = q.items.filter((a) => a.status === 'refused');
  const what = (a: QueuedAction) => t(`crew1102.what.${a.kind}`, { unit: a.unit });
  return (
    <section className="queue-1102" aria-labelledby="queue-1102-h" data-testid="waiting-to-send">
      {q.sentNow.map((s) => (
        <p key={s.id} className="queue-1102-sent" data-testid="queue-sent">
          <Icon name="check" /> {s.text}
        </p>
      ))}
      {q.items.length ? (
        <details open>
          <summary id="queue-1102-h" data-testid="queue-chip">
            <Icon name="clock" />
            <span>
              {t('crew1102.waiting', { count: waiting.length })}
              {refused.length ? ` · ${t('crew1102.refusedCount', { count: refused.length })}` : ''}
            </span>
          </summary>
          <ul aria-labelledby="queue-1102-h">
            {q.items.map((a) => {
              const photos = a.photos.length;
              return (
                <li key={a.id} className={a.status === 'refused' ? 'is-refused' : undefined} data-testid={`queued-${a.kind}-${a.appointmentId}`} data-status={a.status}>
                  <span className="what">{what(a)}</span>
                  {a.status === 'waiting' ? (
                    <span className="sub">
                      {t('crew1102.savedAt', { time: formatClockOf(new Date(a.createdAt).toISOString(), i18n.language) })}
                      {photos ? ` ${t('crew1102.photos', { count: photos })}` : ''} {t('crew1102.sendsWhen')}
                    </span>
                  ) : (
                    <>
                      <span className="sub">
                        <Icon name="alert" /> <b>{t('crew1102.couldNot')}</b> {refusalWords(t, a)}
                      </span>
                      <span className="row">
                        <Button size="sm" variant="secondary" onClick={() => q.retry(a.id)} data-testid={`queue-retry-${a.id}`}>
                          {t('crew1102.tryAgain')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            if (asked === a.id) {
                              setAsked(null);
                              q.remove(a.id);
                            } else setAsked(a.id);
                          }}
                          data-testid={`queue-remove-${a.id}`}
                        >
                          {asked === a.id ? t('crew1102.removeSure') : t('crew1102.remove')}
                        </Button>
                      </span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
